import type { GridColDef } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { Price } from '../../../../components/design/Price';
import { VyTableCell } from '../../../../components/new-design/table/vy-table/VyTableCell';
import { formatNumberWithPrecision } from '../../../../utils/number';
import { RetentionBarCell } from '../../reports-components/RetentionBarCell';
import type { RetentionRow } from '../utils/build-retention-rows';

export const NRR_GRR_REPORT_KEYS = Object.freeze({
  MONTH: 'month',
  STARTING_MRR: 'startingMrr',
  EXPANSION_MRR: 'expansionMrr',
  CONTRACTION_MRR: 'contractionMrr',
  CHURN_MRR: 'churnMrr',
  GRR: 'grr',
  NRR: 'nrr',
} satisfies Record<string, keyof RetentionRow>);

export const NRR_GRR_MANDATORY_COLUMNS_COUNT = 1;

const GRR_BAR_RANGE = { min: 80, max: 100, warningThreshold: 90, dangerThreshold: 80 } as const;
const NRR_BAR_RANGE = { min: 80, max: 120, anchorValue: 100 } as const;

function priceCell(field: 'startingMrr' | 'expansionMrr' | 'contractionMrr' | 'churnMrr') {
  return ({ row }: { row: RetentionRow }) => (
    <Price currency="USD" align="left" fractionDigits={2}>
      {row[field]}
    </Price>
  );
}

function moneyValueFormatter({ value }: { value: unknown }) {
  return formatNumberWithPrecision(Number(value ?? 0), 2);
}

function percentValueFormatter({ value }: { value: unknown }) {
  return `${formatNumberWithPrecision(Number(value ?? 0), 2)}%`;
}

export function useNrrGrrReportColumns(): GridColDef<RetentionRow>[] {
  return useMemo<GridColDef<RetentionRow>[]>(
    () => [
      {
        field: NRR_GRR_REPORT_KEYS.MONTH,
        headerName: 'Posting Date: Month',
        flex: 1,
        minWidth: 180,
        headerAlign: 'left',
        align: 'left',
        hideable: false,
        renderCell: ({ row }) => (
          <VyTableCell value={row.month} containerClassName="justify-start" />
        ),
      },
      {
        field: NRR_GRR_REPORT_KEYS.STARTING_MRR,
        headerName: 'Starting MRR',
        flex: 1,
        minWidth: 160,
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        renderCell: priceCell('startingMrr'),
        valueFormatter: moneyValueFormatter,
      },
      {
        field: NRR_GRR_REPORT_KEYS.EXPANSION_MRR,
        headerName: 'Expansion MRR',
        flex: 1,
        minWidth: 160,
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        renderCell: priceCell('expansionMrr'),
        valueFormatter: moneyValueFormatter,
      },
      {
        field: NRR_GRR_REPORT_KEYS.CONTRACTION_MRR,
        headerName: 'Contraction MRR',
        flex: 1,
        minWidth: 160,
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        renderCell: priceCell('contractionMrr'),
        valueFormatter: moneyValueFormatter,
      },
      {
        field: NRR_GRR_REPORT_KEYS.CHURN_MRR,
        headerName: 'Churn MRR',
        flex: 1,
        minWidth: 160,
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        renderCell: priceCell('churnMrr'),
        valueFormatter: moneyValueFormatter,
      },
      {
        field: NRR_GRR_REPORT_KEYS.NRR,
        headerName: 'NRR',
        flex: 1.2,
        minWidth: 200,
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        renderCell: ({ row }) => (
          <RetentionBarCell
            value={row.nrr}
            minScale={NRR_BAR_RANGE.min}
            maxScale={NRR_BAR_RANGE.max}
            anchorValue={NRR_BAR_RANGE.anchorValue}
          />
        ),
        valueFormatter: percentValueFormatter,
      },
      {
        field: NRR_GRR_REPORT_KEYS.GRR,
        headerName: 'GRR',
        flex: 1.2,
        minWidth: 200,
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        renderCell: ({ row }) => (
          <RetentionBarCell
            value={row.grr}
            minScale={GRR_BAR_RANGE.min}
            maxScale={GRR_BAR_RANGE.max}
            warningThreshold={GRR_BAR_RANGE.warningThreshold}
            dangerThreshold={GRR_BAR_RANGE.dangerThreshold}
          />
        ),
        valueFormatter: percentValueFormatter,
      },
    ],
    [],
  );
}
